import { ReportData } from '../../types';
import { supabase, createExportJob } from './supabaseMock';

export const exportToExcel = async (
  data: ReportData
): Promise<{ jobId: string | null; error: Error | null }> => {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) throw new Error('User session expired.');

    const projectId = data.project.project_id;
    const projectName = data.project.name;
    const userId = session.user.id;

    // 1. Connectivity Guard
    if (!navigator.onLine) {
      throw new Error('No internet connection detected. Please check your network and try again.');
    }

    // 2. Create a placeholder job record
    const { id: jobId, error: createJobError } =
      await createExportJob(userId, projectId, projectName);

    if (createJobError || !jobId) {
      throw new Error('Failed to initialize export job. Please check your database connection.');
    }

    // 3. Trigger the Node.js Export Service
    const exportServiceUrl = (import.meta as any).env?.VITE_EXPORT_SERVICE_URL || 'https://vworx-cs-node-export-service-0002ea029d4a.herokuapp.com';

    let attempts = 0;
    const maxAttempts = 3;
    let lastError = null;

    while (attempts < maxAttempts) {
      try {
        const response = await fetch(`${exportServiceUrl}/generate-export`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            projectId,
            userId,
            projectName,
            jobId,
            origin: window.location.origin
          })
        });

        if (response.ok) return { jobId, error: null };

        const errorData = await response.json();
        lastError = new Error(errorData.error || `HTTP ${response.status}`);
        attempts++;
        if (attempts < maxAttempts) {
          console.warn(`Export attempt ${attempts} failed, retrying in 2s...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } catch (err: any) {
        lastError = err;
        attempts++;
      }
    }

    // If we get here, all retries failed
    await supabase.from('data_exports').update({
      status: 'failed',
      error_message: 'Failed after 3 attempts: ' + (lastError?.message || 'Unknown error'),
      progress_stage: 'Network Failure'
    }).eq('id', jobId);

    return { jobId, error: new Error('Export service is currently unreachable. Please try again in a few minutes.') };

  } catch (error: any) {
    console.error('Export unexpected error:', error);
    return { jobId: null, error };
  }
};
